import { z } from "zod";

const uploadFileStepSchema = z
  .object({
    order: z.literal(1),
    name: z.literal("upload-file"),
    kind: z.literal("action"),
    completedAt: z.iso.datetime().nullable(),
    outcomes: z.tuple([z.literal("uploaded")]),
    outcome: z.literal("uploaded").nullable(),
  })
  .passthrough();

const selectFileTypeStepSchema = z
  .object({
    order: z.literal(2),
    name: z.literal("select-file-type"),
    kind: z.literal("choice"),
    completedAt: z.iso.datetime().nullable(),
    question: z.literal("What type of document is this?"),
    options: z.tuple([
      z.literal("Prime Contract"),
      z.literal("Subcontract"),
      z.literal("Specifications"),
      z.literal("Drawing Set"),
      z.literal("Other"),
    ]),
    answer: z.enum(["Prime Contract", "Subcontract", "Specifications", "Drawing Set", "Other"]).nullable(),
  })
  .passthrough();

const selectStageStepSchema = z
  .object({
    order: z.literal(3),
    name: z.literal("select-stage"),
    kind: z.literal("choice"),
    completedAt: z.iso.datetime().nullable(),
    question: z.literal("Is it signed, or still in negotiation?"),
    options: z.tuple([z.literal("Signed / executed"), z.literal("Still in negotiation")]),
    answer: z.enum(["Signed / executed", "Still in negotiation"]).nullable(),
  })
  .passthrough();

const firstActionStepSchema = z
  .object({
    order: z.literal(4),
    name: z.literal("first-action"),
    kind: z.literal("choice"),
    completedAt: z.iso.datetime().nullable(),
    question: z.literal("And what would you like to do with it first?"),
    options: z.tuple([z.literal("Add a risk review"), z.literal("Just ask questions")]),
    answer: z.enum(["Add a risk review", "Just ask questions"]).nullable(),
  })
  .passthrough();

const inviteCoworkerStepSchema = z
  .object({
    order: z.literal(5),
    name: z.literal("invite-coworker"),
    kind: z.literal("action"),
    completedAt: z.iso.datetime().nullable(),
    outcomes: z.tuple([z.literal("invited"), z.literal("skipped")]),
    outcome: z.enum(["invited", "skipped"]).nullable(),
  })
  .passthrough();

export const trialOnboardingStateV2Schema = z
  .object({
    version: z.literal(2),
    upload: z
      .object({
        projectId: z.string().min(1),
        projectScopeId: z.string().min(1),
        fileId: z.string().min(1).optional(),
      })
      .optional(),
    steps: z.tuple([
      uploadFileStepSchema,
      selectFileTypeStepSchema,
      selectStageStepSchema,
      firstActionStepSchema,
      inviteCoworkerStepSchema,
    ]),
  })
  .passthrough();

export type TrialOnboardingStateV2 = z.infer<typeof trialOnboardingStateV2Schema>;

export const INITIAL_TRIAL_ONBOARDING_STATE: TrialOnboardingStateV2 = {
  version: 2,
  steps: [
    {
      order: 1,
      name: "upload-file",
      kind: "action",
      completedAt: null,
      outcomes: ["uploaded"],
      outcome: null,
    },
    {
      order: 2,
      name: "select-file-type",
      kind: "choice",
      completedAt: null,
      question: "What type of document is this?",
      options: ["Prime Contract", "Subcontract", "Specifications", "Drawing Set", "Other"],
      answer: null,
    },
    {
      order: 3,
      name: "select-stage",
      kind: "choice",
      completedAt: null,
      question: "Is it signed, or still in negotiation?",
      options: ["Signed / executed", "Still in negotiation"],
      answer: null,
    },
    {
      order: 4,
      name: "first-action",
      kind: "choice",
      completedAt: null,
      question: "And what would you like to do with it first?",
      options: ["Add a risk review", "Just ask questions"],
      answer: null,
    },
    {
      order: 5,
      name: "invite-coworker",
      kind: "action",
      completedAt: null,
      outcomes: ["invited", "skipped"],
      outcome: null,
    },
  ],
};

const contextStepSchema = z
  .object({
    name: z.enum(["select-company-type", "select-trade"]),
    answer: z.string().trim().min(1).max(120).nullable(),
    completedAt: z.iso.datetime().nullable(),
    skipped: z.boolean(),
  })
  .passthrough();
const taskOptionSchema = z
  .object({
    slug: z.string().min(1).max(120),
    reason: z.string().min(1).max(160),
    pass_to_skill: z
      .object({ company_type: z.string().optional(), trade: z.string().optional(), role: z.string().optional() })
      .passthrough(),
  })
  .passthrough();
export const trialOnboardingStateV3Schema = trialOnboardingStateV2Schema.extend({
  version: z.literal(3),
  steps: z.tuple([
    uploadFileStepSchema,
    selectFileTypeStepSchema,
    selectStageStepSchema.extend({
      question: z.string(),
      options: z.array(z.string()),
      answer: z.enum(["Signed / executed", "Signed", "Still in negotiation", "Not applicable"]).nullable(),
    }),
    firstActionStepSchema,
    inviteCoworkerStepSchema,
  ]),
  recommendationSteps: z.tuple([
    contextStepSchema.extend({ name: z.literal("select-company-type") }),
    contextStepSchema.extend({ name: z.literal("select-trade") }),
    z
      .object({
        name: z.literal("select-task"),
        completedAt: z.iso.datetime().nullable(),
        skipped: z.boolean(),
        options: z.array(taskOptionSchema).max(3),
        selectedTask: taskOptionSchema.nullable(),
      })
      .passthrough(),
  ]),
});
export type TrialOnboardingStateV3 = z.infer<typeof trialOnboardingStateV3Schema>;
export type TrialOnboardingState = TrialOnboardingStateV2 | TrialOnboardingStateV3;
export const trialOnboardingStateSchema = z.union([trialOnboardingStateV2Schema, trialOnboardingStateV3Schema]);

export function createTrialOnboardingState(recommendationStepsEnabled = false): TrialOnboardingState {
  if (!recommendationStepsEnabled) return structuredClone(INITIAL_TRIAL_ONBOARDING_STATE);
  return trialOnboardingStateV3Schema.parse({
    ...INITIAL_TRIAL_ONBOARDING_STATE,
    version: 3,
    recommendationSteps: [
      { name: "select-company-type", answer: null, completedAt: null, skipped: false },
      { name: "select-trade", answer: null, completedAt: null, skipped: false },
      { name: "select-task", completedAt: null, skipped: false, options: [], selectedTask: null },
    ],
  });
}

export function isTrialTradeStepApplicable(state: TrialOnboardingStateV3): boolean {
  const company = state.recommendationSteps.find((step) => step.name === "select-company-type");
  return (
    company?.name === "select-company-type" &&
    !company.skipped &&
    (company.answer === "Subcontractor" || company.answer === "Specialty Contractor")
  );
}
